/**
 * The KI-Assistent: the shared assistant drawer of @digitalisation/enlixy-comp
 * on the cockpit's chat API, opened by the floating ChatBubble.
 *
 * A conversation follows the page: on a machine or line page it is about that
 * machine or line (the agent gets its context line), and coming back to the
 * page reopens it. History, rename, delete, "open as page" and the example
 * questions come with the shared drawer; the cockpit adds its source cards
 * under the answers.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AssistantDrawer, useAssistant } from '@digitalisation/enlixy-comp/assistant';
import { useCockpitContext } from '../../../hooks/useCockpitContext';
import { ASSISTANT_PAGE, useAssistantUi } from './assistantUiStore';
import { createCockpitBackend, type CockpitProposal } from './cockpitBackend';
import { ContextLabel } from './ContextLabel';
import { useCockpitConversation } from './conversation';
import { track } from '../../../help/learning';

export function CockpitAssistant() {
  const { pathname } = useLocation();
  const isOpen = useAssistantUi((s) => s.isOpen);
  const close = useAssistantUi((s) => s.close);
  const onPage = pathname === ASSISTANT_PAGE;
  // Mounted from the first open on: an answer keeps streaming while the
  // drawer is closed, and the conversation is still there when it reopens.
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (isOpen) setStarted(true);
  }, [isOpen]);
  // The conversations page is the assistant there: no second one on top.
  useEffect(() => {
    if (onPage) close();
  }, [onPage, close]);

  if (!started || onPage) return null;
  return <AssistantDrawerHost />;
}

function AssistantDrawerHost() {
  const navigate = useNavigate();
  const context = useCockpitContext();
  const backend = useMemo(() => createCockpitBackend(), []);
  const cp = useAssistant<CockpitProposal>(backend, {
    contextId: context.contextId,
    onSend: () => track('assistant_asked'),
  });
  const conversation = useCockpitConversation();
  const isOpen = useAssistantUi((s) => s.isOpen);
  const close = useAssistantUi((s) => s.close);
  const setStreaming = useAssistantUi((s) => s.setStreaming);
  const setSessionId = useAssistantUi((s) => s.setSessionId);

  useEffect(() => {
    setStreaming(cp.streaming);
  }, [cp.streaming, setStreaming]);
  useEffect(() => {
    setSessionId(cp.sessionId);
  }, [cp.sessionId, setSessionId]);
  useEffect(
    () => () => {
      setStreaming(false);
      setSessionId(null);
    },
    [setStreaming, setSessionId],
  );

  const openPage = () => {
    close();
    navigate(
      cp.sessionId ? `${ASSISTANT_PAGE}?s=${encodeURIComponent(cp.sessionId)}` : ASSISTANT_PAGE,
    );
  };

  return (
    // z-60 puts the drawer above the cockpit header (z-50), which grows to
    // 280 px on machine and line pages. From 1024 px it starts below the
    // header's top row; below, the package shows it as a full-screen sheet.
    <div className="relative z-[60]">
      <AssistantDrawer
        cp={cp}
        open={isOpen}
        onClose={close}
        contextLabel={<ContextLabel contextId={cp.contextId} />}
        onOpenPage={openPage}
        widthKey="pc.assistant.width"
        top={88}
        conversation={conversation}
      />
    </div>
  );
}
